/**
 * Utilitaires pour le formatage et le nettoyage des nombres
 */

/**
 * Nettoie une valeur numérique en supprimant les zéros non significatifs au début
 * Exemples:
 * - "01" → "1"
 * - "01200" → "1200"
 * - "0.5" → "0.5" (on garde le zéro si c'est un décimal)
 * - "00.5" → "0.5"
 * - "" → "" (vide reste vide)
 * - "0" → "0" (un seul zéro reste)
 * - "0." → "0." (on garde pour permettre la saisie de décimales)
 */
export function cleanNumericInput(value: string): string {
  if (!value || value === '') {
    return '';
  }

  // Si c'est juste "0" ou "0.", on le garde tel quel
  if (value === '0' || value === '0.') {
    return value;
  }

  // Si la valeur contient un point décimal
  if (value.includes('.')) {
    const parts = value.split('.');
    const integerPart = parts[0] || '';
    const decimalPart = parts[1] || '';

    // Nettoyer la partie entière (supprimer les zéros non significatifs)
    const cleanedInteger = integerPart.replace(/^0+/, '') || '0';

    // Reconstruire la valeur
    if (decimalPart === '') {
      // Si on est en train de taper après le point (ex: "0."), on garde le point
      return cleanedInteger + '.';
    } else {
      return cleanedInteger + '.' + decimalPart;
    }
  }

  // Pour les valeurs entières, supprimer les zéros non significatifs
  // mais garder au moins "0" si la valeur était "0"
  const cleaned = value.replace(/^0+/, '');
  return cleaned === '' ? '0' : cleaned;
}

/**
 * Convertit une valeur string en nombre, en gérant les cas vides
 */
export function parseNumericValue(value: string): number {
  if (!value || value === '' || value === '.') {
    return 0;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Vérifie si une valeur string est un nombre valide (non vide et numérique)
 */
export function isValidNumericValue(value: string | number | undefined): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  
  if (typeof value === 'number') {
    return !isNaN(value) && isFinite(value);
  }
  
  if (typeof value === 'string') {
    if (value.trim() === '' || value === '.') {
      return false;
    }
    // Vérifier que la valeur ne contient que des chiffres, un point décimal et éventuellement un signe moins au début
    const numericRegex = /^-?\d*\.?\d+$/;
    return numericRegex.test(value.trim());
  }
  
  return false;
}

